import { PAYMENT_METHOD_ENUM } from '@/enums/payment-method.enum'
import { isSubscriptionType } from '../../enums/payment-links-types.enum'

import DateManager from '@/utils/date-manager.utils'
import MathManager from '@/utils/math-manager.utils'

class PaymentLinksDetailsManger {
  data
  constructor (data) {
    this.data = data
  }

  generalInfoAdapter () {
    const {
      url,
      payment_link_type,
      total_amount,
      status,
      created_at,
      updated_at,
      id,
      name
    } = this.data

    return {
      type: payment_link_type,
      amount: total_amount.value,
      createdAt: created_at,
      updatedAt: updated_at,
      url,
      status,
      id,
      name
    }
  }

  configurationAdapter () {
    const {
      expiration_date: expiresDate,
      cart_settings,
      total_paid_sessions,
      max_paid_sessions,
      customer_settings,
      payment_link_type,
      order_code
    } = this.data

    const planId = isSubscriptionType(payment_link_type)
      ? cart_settings?.recurrences?.[0]?.plan_id
      : undefined

    return {
      planId,
      expirationDate: expiresDate
        ? DateManager.formatToDate(expiresDate)
        : undefined,
      shippingCost: MathManager.formatCurrency(
        cart_settings.shipping_cost.value
      ),
      totalPaidSessions: total_paid_sessions,
      maxPaidSessions: max_paid_sessions,
      link: {
        name: customer_settings.name,
        email: customer_settings.email
      },
      orderCode: order_code
    }
  }

  creditCardAdapter (
    { installments, operation_type, accepted_brands },
    statementDescriptor
  ) {
    const _installments = installments?.map(installment => ({
      number: installment.number,
      total: installment.total.value,
      totalFees: installment.fees.value
    }))
    return {
      operationType: operation_type,
      acceptedBrands: accepted_brands,
      statementDescriptor,
      installments: _installments,
      hasFees: _installments?.some(installment => installment.totalFees > 0)
    }
  }

  boletoAdapter ({ discount, discount_percentage: percentage, due_at, due_in }) {
    return {
      dueIn: DateManager.convertDaysToYears(due_in),
      dueAt: DateManager.formatToDate(due_at),
      discount: discount?.value
        ? MathManager.formatCurrency(discount?.value)
        : 0,
      discountPercentage: MathManager.percentageCalculator(percentage)
    }
  }

  pixAdapter ({
    discount,
    discount_percentage: percentage,
    expires_in,
    expires_at
  }) {
    return {
      expiresIn: DateManager.convertSecondsToHoursOnly(expires_in),
      expiresAt: DateManager.formatToDate(expires_at),
      discount: discount?.value
        ? MathManager.formatCurrency(discount?.value)
        : 0,
      discountPercentage: MathManager.percentageCalculator(percentage)
    }
  }

  paymentMethodsAdapter () {
    const { payment_settings: paymentSettings } = this.data

    const {
      credit_card_settings,
      boleto_settings,
      pix_settings,
      statement_descriptor,
      accepted_payment_methods
    } = paymentSettings

    const { CARTAO_DE_CREDITO, BOLETO, PIX } = PAYMENT_METHOD_ENUM

    const paymentMethodResultMap = {
      [CARTAO_DE_CREDITO]: this.creditCardAdapter(
        credit_card_settings,
        statement_descriptor
      ),
      [BOLETO]: this.boletoAdapter(boleto_settings),
      [PIX]: this.pixAdapter(pix_settings)
    }

    const stringMap = {
      [CARTAO_DE_CREDITO]: 'creditCard',
      [PIX]: 'pix',
      [BOLETO]: 'boleto'
    }

    return accepted_payment_methods.reduce(
      (acc, paymentMethod) => ({
        ...acc,
        [stringMap[paymentMethod]]: paymentMethodResultMap[paymentMethod]
      }),
      {
        acceptedPaymentMethods: accepted_payment_methods
      }
    )
  }

  itemsAdapter () {
    const { cart_settings } = this.data

    return cart_settings.items?.map((item, index) => ({
      name: item.name,
      amount: MathManager.formatCurrency(item.amount.value),
      totalAmount: MathManager.formatCurrency(
        item?.total_item_amount?.value ?? 0
      ),
      description: item.description,
      quantity: item.default_quantity,
      number: index + 1
    }))
  }
}

export default PaymentLinksDetailsManger
